import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  missionResponseSchema,
  missionStatusSchema,
  missionsResponseSchema,
  unitsResponseSchema,
  type CreateMissionRequest,
  type MissionStatus,
} from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { apiPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { useClaimRoles } from '../auth/useClaimRoles.js';
import { ApiError } from '../../components/ApiError.js';
import { ActionError } from '../../components/ActionError.js';
import { formatDate } from '../issuance/FormsPage.js';
import { PLATOON_LEADER_ROLES, STOREROOM_ROLES } from '../../components/RequireRole.js';

const input = 'mt-1 block w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm';
const primary =
  'rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40';

export function MissionStatusPill({ status }: { status: MissionStatus }) {
  const { t } = useTranslation();
  const tone =
    status === 'active' ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
    : status === 'planned' ? 'bg-sky-50 text-sky-800 ring-sky-200'
    : 'bg-slate-100 text-slate-600 ring-slate-200';
  return <span className={`rounded-full px-2 py-0.5 text-xs ring-1 ${tone}`} data-testid="mission-status">{t(`missions.status.${status}`)}</span>;
}

/**
 * Missions in the caller's reach (REQUIREMENTS §2.6 "Mission management"),
 * with what each has drawn, back, and late. Creating one is for the
 * storeroom and platoon commanders; everyone else reads.
 */
export function MissionsPage() {
  const { t, i18n } = useTranslation();
  const { roles } = useClaimRoles();
  const state = useApi('/missions', missionsResponseSchema);
  const canCreate = roles.some((r) => STOREROOM_ROLES.includes(r) || PLATOON_LEADER_ROLES.includes(r));
  const [creating, setCreating] = useState(false);

  if (state.kind === 'loading') return <p className="text-slate-500">{t('home.loading')}</p>;
  if (state.kind === 'error') return <ApiError code={state.code} requestId={state.requestId} />;

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="missions-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-slate-900">{t('missions.title')}</h2>
        {canCreate && !creating && (
          <button type="button" onClick={() => setCreating(true)} className={primary} data-testid="mission-create-open">
            {t('missions.create')}
          </button>
        )}
      </div>
      {creating && (
        <div className="mt-3">
          <CreateMission
            onCreated={() => {
              setCreating(false);
              state.reload();
            }}
            onClose={() => setCreating(false)}
          />
        </div>
      )}
      {state.data.missions.length === 0 && <p className="mt-3 text-sm text-slate-500" data-testid="missions-empty">{t('missions.empty')}</p>}
      <ul className="mt-3 divide-y divide-slate-100">
        {state.data.missions.map((m) => (
          <li key={m.id} className="flex flex-wrap items-start justify-between gap-2 py-3" data-testid="mission-row" data-id={m.id} data-status={m.status}>
            <div>
              <Link to={`/missions/${m.id}`} className="font-medium text-slate-900 underline-offset-4 hover:underline" data-testid="mission-open">
                {m.name}
              </Link>
              <p className="text-sm text-slate-500">
                <span className="latin-inline">{formatDate(m.startAt, i18n.language)}</span>
                {m.endAt && <> – <span className="latin-inline">{formatDate(m.endAt, i18n.language)}</span></>}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 text-xs text-slate-600">
              <MissionStatusPill status={m.status} />
              <span data-testid="mission-counts">
                {t('missions.counts', { drawn: m.drawn, returned: m.returned })}
                {m.overdue > 0 && <span className="ms-1 font-semibold text-red-700" data-testid="mission-overdue">{t('missions.overdueCount', { count: m.overdue })}</span>}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CreateMission({ onCreated, onClose }: { onCreated: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  const units = useApi('/units', unitsResponseSchema);
  const [form, setForm] = useState({ name: '', description: '', startAt: '', endAt: '', status: 'planned' as MissionStatus, unitIds: [] as string[] });
  const create = useAction((body: CreateMissionRequest) => apiPost('/missions', missionResponseSchema, body));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const r = await create.run({
      name: form.name,
      description: form.description.trim() || null,
      startAt: form.startAt ? new Date(form.startAt).toISOString() : null,
      endAt: form.endAt ? new Date(form.endAt).toISOString() : null,
      status: form.status,
      unitIds: form.unitIds,
    });
    if (!r) return;
    onCreated();
  };

  // Only units the caller may draw on are offered — the server refuses the rest.
  const offered = units.kind === 'ready' ? units.data.units.filter((u) => u.active && units.data.viewableUnitIds.includes(u.id)) : [];
  const toggle = (id: string) =>
    setForm((f) => ({ ...f, unitIds: f.unitIds.includes(id) ? f.unitIds.filter((x) => x !== id) : [...f.unitIds, id] }));

  return (
    <form onSubmit={(e) => void submit(e)} className="w-full rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-testid="mission-create-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-slate-600">
          {t('missions.name')}
          <input className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} data-testid="mission-name" />
        </label>
        <label className="text-sm text-slate-600">
          {t('missions.statusLabel')}
          <select className={input} value={form.status} onChange={(e) => setForm({ ...form, status: missionStatusSchema.parse(e.target.value) })} data-testid="mission-status-select">
            {missionStatusSchema.options.filter((s) => s !== 'closed').map((s) => (
              <option key={s} value={s}>{t(`missions.status.${s}`)}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-slate-600">
          {t('missions.startAt')}
          <input type="date" className={`${input} latin-inline`} value={form.startAt} onChange={(e) => setForm({ ...form, startAt: e.target.value })} data-testid="mission-start" />
        </label>
        <label className="text-sm text-slate-600">
          {t('missions.endAt')}
          <input type="date" className={`${input} latin-inline`} value={form.endAt} onChange={(e) => setForm({ ...form, endAt: e.target.value })} data-testid="mission-end" />
        </label>
        <label className="text-sm text-slate-600 sm:col-span-2">
          {t('missions.description')}
          <input className={input} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="mission-description" />
        </label>
      </div>
      <fieldset className="mt-3">
        <legend className="text-sm text-slate-600">{t('missions.units')}</legend>
        <div className="mt-1 flex flex-wrap gap-2">
          {offered.map((u) => (
            <label key={u.id} className={`flex items-center gap-1 rounded-lg px-2 py-1 text-sm ring-1 ${u.type === 'platoon' ? 'ring-slate-400' : 'ring-slate-200'}`}>
              <input type="checkbox" checked={form.unitIds.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`mission-unit-${u.id}`} />
              {u.name}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="mt-3 flex items-center gap-2">
        <button type="submit" disabled={create.busy || form.name.trim().length < 2 || form.unitIds.length === 0} className={primary} data-testid="mission-create-submit">
          {t('missions.createSubmit')}
        </button>
        <button type="button" onClick={onClose} className="text-sm text-slate-500 underline-offset-4 hover:underline">
          {t('gear.close')}
        </button>
      </div>
      <div className="mt-2"><ActionError error={create.error} /></div>
    </form>
  );
}
